import type { AnnotatedJsonSchema, TreeNode } from '@web-config-app/core';
import { findPropertyHostNode } from '@web-config-app/core';
import {
  getResolvedSchemaNodeAtPath,
  resolveSchemaPropertyLabel,
} from '@web-config-app/schema-utils';

type FormatMessageFn = (descriptor: { id: string }) => string;

export interface ResolveLabelForFormPathInputs {
  /**
   * Schema rooted at the same level the form path is rooted at (typically
   * the entity's `attributes` schema). Used for the direct lookup step.
   */
  schema: AnnotatedJsonSchema | undefined;
  /**
   * Data backing the schema, used by `getResolvedSchemaNodeAtPath` to
   * resolve `oneOf` schemas via their discriminator. Optional — direct
   * lookup still works without it for non-oneOf paths.
   */
  attributes?: unknown;
  /**
   * The entity tree's nodeMap. Required for the property-name fallback
   * but not for the direct lookup; resolution gracefully falls back when
   * absent.
   */
  nodeMap: Map<string, TreeNode> | undefined;
  formatMessage: FormatMessageFn;
}

/**
 * Resolve the user-facing label for a form path so backend error strings
 * like `{{CampaignNode}} is required` can be substituted with the
 * property's actual label. Tries two strategies in order:
 *
 * 1. **Direct lookup** — resolve the schema node at the dot path via
 *    `getResolvedSchemaNodeAtPath` (also resolves `oneOf` discriminators
 *    when `attributes` is provided), then read its `x-entity-label`.
 *
 * 2. **Property-name fallback** — backend pointers can be abbreviated
 *    relative to the actual schema layout (e.g.
 *    `/data/attributes/campaignNodes` when the field actually lives at
 *    `attributes.flowTemplate.campaignNodes`). When the direct lookup
 *    fails, scan the navigable nodeMap for a node whose schema has the
 *    leaf segment as a property. Mirrors the heuristic
 *    `findNavigableFormPath` uses for the link target.
 *
 * Each step is independently optional: a missing schema only disables
 * step 1, a missing nodeMap only disables step 2.
 *
 * Returns `undefined` when neither step finds a usable label. Callers
 * should treat that as "no label" — typically meaning the placeholder's
 * inner text is used as the fallback.
 */
export const resolveLabelForFormPath = (
  formPath: string,
  { schema, attributes, nodeMap, formatMessage }: ResolveLabelForFormPathInputs,
): string | undefined => {
  if (!formPath) return undefined;

  // 1. Direct lookup — works when the pointer matches the schema layout.
  if (schema) {
    const directNode = getResolvedSchemaNodeAtPath(
      formPath,
      schema,
      attributes,
    );
    const directLabel = resolveSchemaPropertyLabel(directNode, formatMessage);
    if (directLabel) return directLabel;
  }

  // 2. Property-name fallback — gated only on the nodeMap so a missing
  //    schema doesn't short-circuit this branch.
  if (!nodeMap) return undefined;
  const segments = formPath.split('.');
  const leafSegment = segments[segments.length - 1];
  const hostNode = findPropertyHostNode(leafSegment, nodeMap);
  if (!hostNode) return undefined;

  const propertySchema = hostNode.data?.schema?.properties?.[leafSegment];
  return resolveSchemaPropertyLabel(propertySchema, formatMessage);
};
